import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabaseClient'
import type { FullInitialOnboardingData } from '@/lib/validation/initialOnboarding'

interface InitialOnboardingState {
  id?: string
  user_id?: string
  answers: Partial<FullInitialOnboardingData>
  step: number
  done: boolean
  created_at?: string
  updated_at?: string
}

export function useInitialOnboarding() {
  const [data, setData] = useState<InitialOnboardingState | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Load existing data on mount
  useEffect(() => {
    loadData()
  }, [])

  // Listen for completion events from other components
  useEffect(() => {
    const handleCompletion = (event: CustomEvent) => {
      const completionData = event.detail;
      setData(prev => ({ 
        ...prev, 
        ...completionData,
        id: prev?.id,
        user_id: prev?.user_id,
        created_at: prev?.created_at,
        updated_at: new Date().toISOString()
      }));
    };

    window.addEventListener('initialOnboardingCompleted', handleCompletion as EventListener);
    
    return () => {
      window.removeEventListener('initialOnboardingCompleted', handleCompletion as EventListener);
    };
  }, [])

  const loadData = async () => {
    try {
      setIsLoading(true)
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        setIsLoading(false)
        return
      }

      // Load existing onboarding data
      const { data: existingData, error } = await supabase
        .from('init_onb')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        setIsLoading(false)
        return
      }

      if (existingData) {
        setData(existingData)
      }
    } catch (error) {
      console.error('Error in loadData:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const save = async (updates: Partial<Pick<InitialOnboardingState, 'answers' | 'step' | 'done'>>) => {
    try {
      setIsSaving(true)

      // Get current user
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('No authenticated user')
      }

      // Merge answers properly, ensuring we don't lose existing data
      const currentAnswers = data?.answers || {};
      const newAnswers = updates.answers || {};
      const mergedAnswers = { ...currentAnswers, ...newAnswers };

      const payload = {
        user_id: user.id,
        answers: mergedAnswers,
        step: updates.step ?? data?.step ?? 0,
        done: updates.done ?? data?.done ?? false,
      }

      const { data: savedData, error } = await supabase
        .from('init_onb')
        .upsert(payload, { onConflict: 'user_id' })
        .select()
        .single()

      if (error) {
        console.error('Error saving onboarding data:', error)
        throw error
      }

      setData(savedData)
      
      return savedData
    } catch (error) {
      console.error('Error in save:', error)
      throw error
    } finally {
      setIsSaving(false)
    }
  }

  const complete = async () => {
    try {
      setIsSaving(true)

      // Get current user
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('No authenticated user')
      }

      const payload = {
        user_id: user.id,
        answers: data?.answers || {}, // This will be overridden by the caller if needed
        step: data?.step ?? 0,
        done: true,
      }

      const { data: completedData, error } = await supabase
        .from('init_onb')
        .upsert(payload, { onConflict: 'user_id' })
        .select()
        .single()

      if (error) {
        console.error('Error completing onboarding:', error)
        throw error
      }

      setData(completedData)
    } catch (error) {
      console.error('Error in complete:', error)
      throw error
    } finally {
      setIsSaving(false)
    }
  }

  return {
    data,
    save,
    complete,
    isSaving,
    isLoading,
    refetch: loadData,
  }
}